import { FormControl, MenuItem, FormHelperText } from '@mui/material';
import { FC, useEffect } from 'react';
import { ISalesEmailTemplateLookup, EventType } from '../../models';
import { SMBSelect } from '../input';
import { camelCaseToWords } from '../../helpers/format';

interface IEmailTemplateDropdownProps {
  value: string | number;
  handleChange: (val: string) => void;
  isDisabled?: boolean;
  setOptions?: (val: ISalesEmailTemplateLookup[]) => void;
  isRequired?: boolean;
  message?: string;
  hasError?: boolean;
  eventType?: string;
  isLoadingEmailTemplateLookup?: boolean;
  emailTemplates: ISalesEmailTemplateLookup[];
  setHasManuallySelectedTemplate?: (value: boolean) => void;
}

export const EmailTemplateDropdown: FC<IEmailTemplateDropdownProps> = ({
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  message,
  hasError,
  eventType,
  isLoadingEmailTemplateLookup,
  emailTemplates,
  setHasManuallySelectedTemplate
}) => {
  // Find default template based on eventType
  useEffect(() => {
    if (eventType && emailTemplates && emailTemplates.length > 0 && value === '') {
      const event = eventType === EventType.ClosingAppointment ? 'ClosingEvent' : eventType;
      const formattedEventType = camelCaseToWords(event);

      // Find template that contains the formatted event type
      const defaultTemplate = emailTemplates.find(
        template =>
          template.templateName.includes(`Default ${formattedEventType}`) ||
          template.templateName.includes(formattedEventType)
      );

      if (defaultTemplate) {
        handleChange(defaultTemplate.emailTemplateId.toString());
      }
    }
  }, [emailTemplates, eventType, handleChange, value]);

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired} error={hasError}>
      <SMBSelect
        disabled={isDisabled || isLoadingEmailTemplateLookup}
        label={isLoadingEmailTemplateLookup ? 'Loading...' : 'Email Template'}
        value={value}
        size="small"
        required={isRequired}
        onChange={({ target: { value } }) => {
          handleChange(value as string);
          if (setHasManuallySelectedTemplate) {
            setHasManuallySelectedTemplate(true);
          }
        }}
      >
        {!isLoadingEmailTemplateLookup && emailTemplates && emailTemplates.length === 0 && (
          <MenuItem disabled>No data available</MenuItem>
        )}
        {!isLoadingEmailTemplateLookup &&
          emailTemplates &&
          emailTemplates.map((d, index) => {
            return (
              <MenuItem key={d.emailTemplateId} value={d.emailTemplateId}>
                {d.templateName}
              </MenuItem>
            );
          })}
      </SMBSelect>
      {message && <FormHelperText error>{message}</FormHelperText>}
    </FormControl>
  );
};
